import { DashboardStats, Tenant, CreateTenantPayload, TenantQuota, TenantDetail, TenantUser, TenantStats, SubscriptionPlan, BillingDashboard, TenantTokenStatus } from '@/types'
import { devLog, errorLog } from '@/utils/logger'

export class ApiError extends Error {
  public status: number
  public payload: unknown

  constructor(status: number, message: string, payload?: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.payload = payload
  }
}

// URL da API - usa proxy local em desenvolvimento
const API_BASE_URL = (import.meta.env.DEV
  ? ''
  : (import.meta.env.VITE_API_URL || 'https://api.quadradois.com.br'))

const AUTH_STORAGE_KEY = 'auth-storage'

const DEFAULT_ERROR_MESSAGES: Record<number, string> = {
  400: 'Requisição inválida. Verifique os dados enviados.',
  401: 'Credenciais inválidas ou sessão expirada.',
  403: 'Você não possui permissão para realizar esta ação.',
  404: 'Recurso não encontrado.',
  409: 'Operação em conflito com o estado atual dos dados.',
  422: 'Dados inválidos. Corrija os campos destacados.',
  429: 'Muitas requisições. Tente novamente em instantes.',
  500: 'Erro interno do servidor. Tente novamente mais tarde.'
}

type PersistedAuthState = {
  state?: {
    token?: string | null
  }
}

export type ZapLeadStatus =
  | 'novo'
  | 'qualificando'
  | 'contatado'
  | 'interessado'
  | 'sem_perfil'
  | 'cliente_existente'
  | 'indisponivel'
  | 'erro'

export type ZapLeadRunStatus = 'QUEUED' | 'RUNNING' | 'DONE' | 'PARTIAL' | 'FAILED' | 'CANCELLED'

export type ZapLeadSummary = {
  querySignature?: string | null
  pagesProcessed?: number
  totalPagesFetched?: number
  totalListings?: number
  totalProspects?: number
  totalAdvertisers?: number
  targetProspects?: number
  duplicatesIgnored?: number
  prospectsWithContact?: number
  prospectsWithCreci?: number
  enrichedProspects?: number
  enrichedAdvertisers?: number
  errorProspects?: number
  errorAdvertisers?: number
  errorEvents?: number
  enrichmentPending?: boolean
  enrichmentBatchSize?: number
  enrichmentResumeDelaySeconds?: number
  resumePending?: boolean
  resumeDelaySeconds?: number
  transientBlocks?: number
  transientBlockRate?: number
  sourceHealth?: string
  durationMs?: number
  updatedAt?: string | null
}

export type ZapLeadPortfolio = {
  totalListings?: number
  availabilityStatus?: string
  lastSnapshotAt?: string | null
}

export type ZapLeadSignals = {
  listingCount?: number | null
  hasContact?: boolean
  hasCreci?: boolean
  commercialScore?: number | null
  suggestedQueue?: string | null
  neighborhoods?: string[]
  unitTypes?: string[]
  priceRange?: { min?: number | null; max?: number | null } | null
  accountTier?: string | null
  averageLqs?: number | null
}

export type ZapLeadQueue = 'prioridade_a' | 'prioridade_b' | 'prioridade_c' | 'clientes'
export type ZapLeadSort = 'updated_desc' | 'score_desc' | 'evidence_desc'

export type ZapLeadActivityType =
  | 'contato'
  | 'whatsapp'
  | 'ligacao'
  | 'convite_enviado'
  | 'nota'
  | 'cadastro'
  | 'ativacao'
  | 'score'
  | 'fila'
  | 'temperatura'
  | 'addon'

export type ZapLeadProspectItem = {
  prospectId: number
  advertiserId?: string | null
  name?: string | null
  licenseNumber?: string | null
  logoUrl?: string | null
  coverUrl?: string | null
  qualityScore?: number | null
  description?: string | null
  status: ZapLeadStatus
  isExistingTenant?: boolean
  existingTenantId?: number | null
  contactName?: string | null
  contactPhone?: string | null
  contactEmail?: string | null
  contactSource?: string | null
  portfolio?: ZapLeadPortfolio | null
  signals?: ZapLeadSignals | null
  evidenceCount?: number | null
  updatedAt?: string | null
  activation?: ZapLeadActivation | null
}

export type ZapLeadResultItem = ZapLeadProspectItem

export type ZapLeadRun = {
  runId: number
  status: ZapLeadRunStatus | string
  querySignature?: string | null
  filters: Record<string, unknown>
  summary: ZapLeadSummary
  errorSnapshot?: unknown
  createdAt: string | null
  updatedAt: string | null
}

export type ZapLeadRunsPage = {
  page: number
  pageSize: number
  total: number
  items: ZapLeadRun[]
}

export type ZapLeadSearchPayload = {
  stateAcronym?: string
  addressCity?: string
  addressNeighborhood?: string
  addressZone?: string
  addressStreet?: string
  addressType?: string
  unitType?: string
  priceMin?: number
  priceMax?: number
  usableAreasMin?: number
  usableAreasMax?: number
  bedrooms?: number
  parkingSpaces?: number
  business?: string
  listingType?: string
  target_prospects?: number
  max_pages?: number
  size?: number
  max_enrichments?: number
}

export type GetZapLeadResultsPayload = {
  page?: number
  pageSize?: number
  status?: ZapLeadStatus
  city?: string
  stateAcronym?: string
  existingTenant?: boolean
  queue?: ZapLeadQueue
  hasContact?: boolean
  hasCreci?: boolean
  sort?: ZapLeadSort
}

export type ZapLeadResultsPage = {
  runId: number
  page: number
  pageSize: number
  total: number
  items: ZapLeadProspectItem[]
}

export type ZapLeadProspectsPage = Omit<ZapLeadResultsPage, 'runId'>

export type ZapLeadEvidence = {
  id: number
  listing_id?: string
  city?: string | null
  neighborhood?: string | null
  status?: string | null
  enrichment_error?: string | null
  search_page?: number | null
}

export type ZapLeadActivity = {
  activityType: string
  note?: string | null
  temperature?: string | null
  reason?: string | null
  nextActionAt?: string | null
  inviteLink?: string | null
  firstValueAction?: string | null
  metadata?: Record<string, unknown>
  createdAt?: string | null
}

export type ZapLeadActivation = {
  firstAccess?: boolean
  propertyPublished?: boolean
  actions?: string[]
}

export type ZapLeadProspectDetail = ZapLeadProspectItem & {
  evidences?: ZapLeadEvidence[]
  activities?: ZapLeadActivity[]
}

export type ZapLeadProspectUpdatePayload = {
  status?: ZapLeadStatus
  activityType?: ZapLeadActivityType
  isExistingTenant?: boolean
  existingTenantId?: number | null
  note?: string
  temperature?: string
  reason?: string
  nextActionAt?: string
  inviteLink?: string
  firstValueAction?: string
  contactName?: string
  contactPhone?: string
  contactEmail?: string
  contactSource?: string
}

class ApiService {
  private authToken: string | null = null

  private resolveToken(): string | null {
    if (this.authToken) {
      return this.authToken
    }

    const directToken = localStorage.getItem('token')
    if (directToken) {
      this.authToken = directToken
      return directToken
    }

    const persisted = localStorage.getItem(AUTH_STORAGE_KEY)
    if (persisted) {
      try {
        const parsed = JSON.parse(persisted) as PersistedAuthState
        const persistedToken = parsed?.state?.token ?? null
        if (persistedToken) {
          this.authToken = persistedToken
          // Garantir que o token também esteja disponível diretamente
          localStorage.setItem('token', persistedToken)
          return persistedToken
        }
      } catch (error) {
        errorLog('Não foi possível interpretar auth-storage:', error)
      }
    }

    return null
  }

  public setAuthToken(token: string | null) {
    this.authToken = token

    if (token) {
      localStorage.setItem('token', token)
    } else {
      localStorage.removeItem('token')
    }
  }

  public async request<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const token = this.resolveToken()

    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      ...options,
    }

    devLog(`🔗 Chamada API: ${endpoint}`, {
      hasToken: !!token,
      tokenPreview: token ? token.substring(0, 20) + '...' : 'none',
    })

    const response = await fetch(`${API_BASE_URL}${endpoint}`, config)

    if (!response.ok) {
      const errorText = await response.text()
      let parsedPayload: any = null
      let message = DEFAULT_ERROR_MESSAGES[response.status] ?? 'Erro ao comunicar com a API.'

      if (errorText) {
        try {
          parsedPayload = JSON.parse(errorText)
          message = parsedPayload?.error || parsedPayload?.message || message
        } catch (parseError) {
          parsedPayload = errorText
          message = errorText
        }
      }

      console.error(`❌ Erro de API [${response.status}]:`, parsedPayload ?? errorText)
      throw new ApiError(response.status, message, parsedPayload)
    }

    if (response.status === 204 || response.headers.get('content-length') === '0') {
      devLog(`✅ Sucesso API (sem conteúdo): ${endpoint}`)
      return undefined as T
    }

    const contentType = response.headers.get('content-type') || ''
    try {
      const data = contentType.includes('application/json')
        ? await response.json()
        : await response.text()

      devLog(`✅ Sucesso API: ${endpoint}`, data)
      return data as T
    } catch (parseError) {
      const rawText = await response.text().catch(() => '')
      errorLog('Falha ao interpretar resposta da API', { endpoint, parseError, rawText })
      throw new ApiError(response.status, 'Resposta inválida da API.', rawText || parseError)
    }
  }

  // Dashboard APIs
  async getDashboardStats(): Promise<DashboardStats> {
    const response = await this.request<any>('/api/admin/dashboard/overview')

    const payload = response?.data ?? response ?? {}
    const tenants = payload.tenants ?? {}
    const users = payload.users ?? {}
    const properties = payload.properties ?? {}

    return {
      total_tenants: tenants.total ?? 0,
      total_users: users.total ?? 0,
      total_properties: properties.total ?? 0,
      active_tenants: tenants.active ?? tenants.total ?? 0,
      growth_rate: tenants.growth_rate ?? tenants.growth_30d ?? 0,
      revenue_trend: payload.revenue_trend ?? 0
    }
  }

  async getTenants(): Promise<Tenant[]> {
    const response = await this.request<any>('/api/tenants/list')
    const tenantsPayload: any[] = response?.tenants ?? []

    return tenantsPayload.map((tenant: any) => {
      const generatedTenantId = `tenant_${String(tenant.id ?? '').padStart(3, '0')}`
      const createdAt = tenant.created_at || tenant.createdAt
      const updatedAt = tenant.updated_at || tenant.updatedAt

      return {
        id: tenant.id,
        tenant_id: tenant.tenant_id || generatedTenantId,
        tenant_type: tenant.tenant_type || 'PJ',
        name: tenant.name,
        email: tenant.email || undefined,
        phone: tenant.phone || undefined,
        address: tenant.address || undefined,
        city: tenant.city || undefined,
        state: tenant.state || undefined,
        zip_code: tenant.zip_code || undefined,
        status: tenant.is_active ? 'ativo' : 'inativo',
        db_name: tenant.db_name || tenant.schema || undefined,
        created_at: createdAt ?? new Date().toISOString(),
        updated_at: updatedAt ?? null,
        is_active: tenant.is_active,
        subscription_plan: tenant.subscription_plan || undefined,
        health_score: tenant.health_score ?? undefined,
        users_count: tenant.users_count ?? 0,
        properties_count: tenant.properties_count ?? 0,
        is_master: tenant.is_master ?? false,
      }
    })
  }

  async updateTenant(tenantId: number, payload: Partial<Tenant>) {
    return this.request(`/api/tenants/${tenantId}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    })
  }

  async updateTenantStatus(tenantId: number, isActive: boolean) {
    return this.request(`/api/tenants/${tenantId}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ is_active: isActive }),
    })
  }

  async updateTenantModules(
    tenantId: number,
    modules: {
      gamification_enabled?: boolean
      elyon_enabled?: boolean
      site_enabled?: boolean
      active_offer_enabled?: boolean
      cockpit_enabled?: boolean
      lancamentos_enabled?: boolean
      parcerias_enabled?: boolean
      canalpro_enabled?: boolean
      portal_proprietario_enabled?: boolean
      // Onda 7 (PLANO_MIGRACAO_COMPLETA_V2): toggle Agentes IA
      ai_agents_enabled?: boolean
      // Master switch da integração com o portal 62imoveis
      portal_62imoveis_enabled?: boolean
      // Master switch do módulo de Aluguel/Locação
      aluguel_enabled?: boolean
      [key: string]: boolean | undefined
    }
  ) {
    return this.request<{ success: boolean; message: string; modules: any }>(`/api/tenants/${tenantId}/modules`, {
      method: 'PUT',
      body: JSON.stringify(modules),
    })
  }

  async updateTenantPortalModes(
    tenantId: number,
    modes: {
      canalpro_imoveis_mode?: 'xml' | 'graphql'
      canalpro_leads_mode?: 'webhook' | 'graphql'
      canalpro_leads_poll_interval_minutes?: number
      portal_62imoveis_imoveis_mode?: 'xml' | 'graphql'
      portal_62imoveis_leads_mode?: 'webhook' | 'graphql'
    }
  ) {
    return this.request<{ success: boolean; message: string; modes: Record<string, string> }>(
      `/api/tenants/${tenantId}/portal-modes`,
      {
        method: 'PATCH',
        body: JSON.stringify(modes),
      }
    )
  }

  async searchZapLeads(payload: ZapLeadSearchPayload): Promise<ZapLeadRun> {
    const response = await this.request<any>('/api/admin/zap-leads/search', {
      method: 'POST',
      body: JSON.stringify(payload),
    })

    return {
      runId: response.runId,
      status: response.status,
      querySignature: response.querySignature,
      filters: response.filters || {},
      summary: response.summary || {},
      errorSnapshot: response.errorSnapshot,
      createdAt: response.createdAt || null,
      updatedAt: response.updatedAt || null,
    }
  }

  async getZapLeadRun(runId: number): Promise<ZapLeadRun> {
    const response = await this.request<any>(`/api/admin/zap-leads/runs/${runId}`)

    return {
      runId: response.runId,
      status: response.status,
      querySignature: response.querySignature,
      filters: response.filters || {},
      summary: response.summary || {},
      errorSnapshot: response.errorSnapshot,
      createdAt: response.createdAt || null,
      updatedAt: response.updatedAt || null,
    }
  }

  async getZapLeadRuns(params: { page?: number; pageSize?: number; status?: ZapLeadRunStatus } = {}): Promise<ZapLeadRunsPage> {
    const query = new URLSearchParams()
    if (params.page != null) query.set('page', String(params.page))
    if (params.pageSize != null) query.set('pageSize', String(params.pageSize))
    if (params.status) query.set('status', params.status)

    const response = await this.request<any>(`/api/admin/zap-leads/runs?${query.toString()}`)

    return {
      page: response.page || 1,
      pageSize: response.pageSize || 25,
      total: response.total || 0,
      items: response.items || [],
    }
  }

  async getZapLeadResults(
    runId: number,
    params: GetZapLeadResultsPayload,
  ): Promise<ZapLeadResultsPage> {
    const query = new URLSearchParams()
    if (params.page != null) query.set('page', String(params.page))
    if (params.pageSize != null) query.set('pageSize', String(params.pageSize))
    if (params.status) query.set('status', params.status)
    if (params.city) query.set('city', params.city)
    if (params.stateAcronym) query.set('stateAcronym', params.stateAcronym)
    if (params.existingTenant != null) query.set('existingTenant', String(params.existingTenant))
    if (params.queue) query.set('queue', params.queue)
    if (params.hasContact != null) query.set('hasContact', String(params.hasContact))
    if (params.hasCreci != null) query.set('hasCreci', String(params.hasCreci))
    if (params.sort) query.set('sort', params.sort)

    const response = await this.request<any>(`/api/admin/zap-leads/runs/${runId}/prospects?${query.toString()}`)

    return {
      runId: response.runId,
      page: response.page || 1,
      pageSize: response.pageSize || 25,
      total: response.total || 0,
      items: response.items || [],
    }
  }

  async getZapLeadProspects(params: GetZapLeadResultsPayload): Promise<ZapLeadProspectsPage> {
    const query = new URLSearchParams()
    if (params.page != null) query.set('page', String(params.page))
    if (params.pageSize != null) query.set('pageSize', String(params.pageSize))
    if (params.status) query.set('status', params.status)
    if (params.city) query.set('city', params.city)
    if (params.stateAcronym) query.set('stateAcronym', params.stateAcronym)
    if (params.existingTenant != null) query.set('existingTenant', String(params.existingTenant))
    if (params.queue) query.set('queue', params.queue)
    if (params.hasContact != null) query.set('hasContact', String(params.hasContact))
    if (params.hasCreci != null) query.set('hasCreci', String(params.hasCreci))
    if (params.sort) query.set('sort', params.sort)

    const response = await this.request<any>(`/api/admin/zap-leads/prospects?${query.toString()}`)

    return {
      page: response.page || 1,
      pageSize: response.pageSize || 25,
      total: response.total || 0,
      items: response.items || [],
    }
  }

  async getZapLeadProspect(prospectId: number): Promise<ZapLeadProspectDetail> {
    return this.request<ZapLeadProspectDetail>(`/api/admin/zap-leads/prospects/${prospectId}`)
  }

  async rerunZapLeads(runId: number, payload: ZapLeadSearchPayload): Promise<ZapLeadRun> {
    const response = await this.request<any>(`/api/admin/zap-leads/runs/${runId}/rerun`, {
      method: 'POST',
      body: JSON.stringify(payload),
    })

    return {
      runId: response.runId,
      status: response.status,
      querySignature: response.querySignature,
      filters: response.filters || {},
      summary: response.summary || {},
      errorSnapshot: response.errorSnapshot,
      createdAt: response.createdAt || null,
      updatedAt: response.updatedAt || null,
    }
  }

  async cancelZapLeadsRun(runId: number): Promise<ZapLeadRun> {
    const response = await this.request<any>(`/api/admin/zap-leads/runs/${runId}/cancel`, {
      method: 'POST',
    })

    return {
      runId: response.runId,
      status: response.status,
      querySignature: response.querySignature,
      filters: response.filters || {},
      summary: response.summary || {},
      errorSnapshot: response.errorSnapshot,
      createdAt: response.createdAt || null,
      updatedAt: response.updatedAt || null,
    }
  }

  async updateZapLeadProspect(prospectId: number, payload: ZapLeadProspectUpdatePayload): Promise<{ prospectId: number; status: ZapLeadStatus; updatedAt?: string | null }> {
    return this.request<{ prospectId: number; status: ZapLeadStatus; updatedAt?: string | null }>(
      `/api/admin/zap-leads/prospects/${prospectId}`,
      {
        method: 'PATCH',
        body: JSON.stringify(payload),
      }
    )
  }

  async getZapLeadExportCsv(runId?: number, status?: ZapLeadStatus): Promise<string> {
    const query = new URLSearchParams()
    if (runId != null) query.set('runId', String(runId))
    if (status) query.set('status', status)
    query.set('format', 'csv')

    return this.request<string>(`/api/admin/zap-leads/prospects/export?${query.toString()}`)
  }

  async deleteTenant(tenantId: number) {
    return this.request(`/api/tenants/${tenantId}`, {
      method: 'DELETE',
    })
  }

  async createTenant(payload: CreateTenantPayload) {
    return this.request('/api/tenants/create', {
      method: 'POST',
      body: JSON.stringify(payload)
    })
  }

  async getTenantsQuotas(): Promise<TenantQuota[]> {
    const response = await this.request<any>('/api/tenants/quotas')
    return response?.tenants ?? []
  }

  async getSystemHealth() {
    return this.request('/api/health')
  }

  // Auth APIs (já existentes no store, mas centralizando aqui)
  async login(username: string, password: string) {
    const response = await this.request<{ access_token: string, user: any }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password })
    })

    // Salvar o token e manter em memória
    if (response.access_token) {
      this.setAuthToken(response.access_token)
      devLog('🔐 Token armazenado no ApiService:', response.access_token.substring(0, 20) + '...')
    }

    // Normalizar o retorno para manter consistência
    return {
      token: response.access_token,
      user: response.user
    }
  }

  async getProfile() {
    return this.request<any>('/auth/me')
  }

  // Tenant Detail APIs
  async getTenantDetail(tenantId: number): Promise<TenantDetail> {
    return this.request<TenantDetail>(`/api/tenants/${tenantId}`)
  }

  async getTenantUsers(tenantId: number): Promise<{ tenant: { id: number; name: string }; users: TenantUser[]; total: number }> {
    return this.request(`/api/tenants/${tenantId}/users`)
  }

  async getTenantStats(tenantId: number): Promise<TenantStats> {
    return this.request<TenantStats>(`/api/tenants/${tenantId}/stats`)
  }

  async getTenantTokenStatus(tenantId: number): Promise<TenantTokenStatus> {
    return this.request<TenantTokenStatus>(`/api/tenants/${tenantId}/token-status`)
  }

  // Subscription & Billing APIs
  async getSubscriptionPlans(showAll: boolean = false): Promise<{ plans: SubscriptionPlan[]; total: number }> {
    const params = showAll ? '?show_all=true' : ''
    return this.request(`/api/subscriptions/plans${params}`)
  }

  async createSubscriptionPlan(payload: any): Promise<{ message: string; plan: SubscriptionPlan }> {
    return this.request(`/api/subscriptions/plans`, {
      method: 'POST',
      body: JSON.stringify(payload)
    })
  }

  async getSubscriptionPlan(planId: number): Promise<SubscriptionPlan> {
    return this.request<SubscriptionPlan>(`/api/subscriptions/plans/${planId}`)
  }

  async updateSubscriptionPlan(planId: number, payload: Partial<SubscriptionPlan>): Promise<{ message: string; plan: SubscriptionPlan }> {
    return this.request(`/api/subscriptions/plans/${planId}`, {
      method: 'PUT',
      body: JSON.stringify(payload)
    })
  }

  async deleteSubscriptionPlan(planId: number): Promise<{ message: string }> {
    return this.request(`/api/subscriptions/plans/${planId}`, {
      method: 'DELETE'
    })
  }

  async getBillingDashboard(): Promise<BillingDashboard> {
    return this.request<BillingDashboard>(`/api/subscriptions/dashboard`)
  }

  async assignPlanToTenant(tenantId: number, payload: any): Promise<{ message: string; subscription: any }> {
    return this.request(`/api/subscriptions/tenant/${tenantId}/assign`, {
      method: 'POST',
      body: JSON.stringify(payload)
    })
  }

  async getTenantSubscription(tenantId: number): Promise<{ has_subscription: boolean; subscription?: any }> {
    return this.request(`/api/subscriptions/tenant/${tenantId}`)
  }

  // User password management
  async changeUserPassword(userId: number, newPassword: string): Promise<{ message: string; user: any }> {
    return this.request(`/api/users/${userId}/password`, {
      method: 'PUT',
      body: JSON.stringify({ new_password: newPassword })
    })
  }

  async deleteUser(userId: number): Promise<{ message: string }> {
    return this.request(`/api/users/${userId}`, {
      method: 'DELETE'
    })
  }
}

export const apiService = new ApiService()
